import type { FC } from "react";
import { useEffect, useMemo, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import styled from "styled-components";

import { AreaGraph, LEGEND_POSITIONS, LineGraph } from "@similarweb/pro-logic-components";
import type { GenericGraphProps } from "@similarweb/pro-logic-components/dist/graphs/types";

import { type ICurrency, currenciesList } from "app/common/constants/currencies";
import { ChartContainerNoData } from "app/components/keyword-research/clicks-over-time-graph/styledComponents";
import type { IDataType } from "app/components/React/GraphTypeSwitcher/GraphTypeSwitcher";
import { abbrNumberFilter, minAbbrNumberFilter, percentageSignFilter } from "app/filters/ngFilters";
import { LoadingSpinner } from "app/pages/keyword-analysis/TotalTrafficPage/StyledComponents";
import { SwitchersContainer } from "app/pages/website-analysis/traffic-sources/search/overview/SearchOverviewGraph/Components/StyledComponents";
import { CurrencySelector } from "app/pages/website-analysis/TrafficAndEngagement/ChartUtilities/CurrencySelector";
import DurationService from "app/services/DurationService";
import { mapSearchAdsSpend } from "app/services/searchAdsSpend/searchAdsSpendMapper";
import { isUnsupportedRange, splitDomains } from "app/services/searchAdsSpend/searchAdsSpendParams";
import { useSearchAdsSpendQuery } from "app/services/searchAdsSpend/useSearchAdsSpendQuery";
import { allTrackers } from "app/services/track/track";
import { NoData } from "pro-features/components/NoData/src/NoData";
import { CenteredFlexColumn } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { GraphTypeSwitcher } from "../../Helpers/GraphTypeSwitcher";
import { isAllZeroSeries } from "../../Helpers/helpers";
import { availableDataTypes } from "../../Helpers/SearchOverviewGraphConfig";

import { getPpcChartConfig } from "./config/config";
import { SpendRatesNote } from "./SpendRatesNote";
import { transformPpcSpendData } from "./transformPpcSpendData";
import { UnsupportedRangeNotice } from "./UnsupportedRangeNotice";

const PpcSpendChartWrapper = styled.div`
    margin-bottom: 24px;
`;

const ButtonsContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
`;

const LoaderContainer = styled(CenteredFlexColumn)`
    padding: 26px 0 106px 0;
    height: 144px;
`;

interface IPpcSpendChartProps {
    isShouldFetch?: boolean;
}

const PpcSpendChart: FC<IPpcSpendChartProps> = ({ isShouldFetch = true }) => {
    const { params } = useCurrentStateAndParams();
    const apiParams = proNavigator.getApiParams(params);
    const isCompare = apiParams.key.split(",").length > 1;

    const [selectedCurrency, setSelectedCurrency] = useState<ICurrency>(() => {
        const { performanceOverTimeChartCurrency } = proNavigator.getParams();
        return performanceOverTimeChartCurrency
            ? currenciesList.find((currency) => currency.name === performanceOverTimeChartCurrency)
            : currenciesList[0];
    });
    const [graphType, setGraphType] = useState<IDataType>(availableDataTypes[0]);
    const isPercentage = graphType.value !== "numbers";

    const { from, to, isWindow } = DurationService.getDurationData(params.duration, params.comparedDuration).forAPI;

    const domains = useMemo(() => splitDomains(apiParams.key), [apiParams.key]);

    const { data, isLoading, isError, error } = useSearchAdsSpendQuery(
        {
            country: Number(params.country),
            from,
            to,
            keys: domains,
            isWindow,
            webSource: params.webSource,
        },
        { enabled: isShouldFetch },
    );

    const spend = useMemo(
        () => mapSearchAdsSpend(data, { currency: selectedCurrency.name, webSource: params.webSource }),
        [data, selectedCurrency.name, params.webSource],
    );

    const [lineGraphData, setLineGraphData] = useState<GenericGraphProps>([]);

    useEffect(() => {
        setLineGraphData(data ? transformPpcSpendData(spend, { isPercentage, isCompare }) : []);
    }, [data, spend, isPercentage, isCompare]);

    const chartConfig = useMemo(() => {
        return getPpcChartConfig({
            // @ts-expect-error FIXME: Type 'GenericGraphProps' is not assignable to type 'SimplifiedSeries<SeriesLineOptions>'.
            chartData: lineGraphData,
            selectedCurrency,
            isPercentage,
            volumeFilter: isPercentage
                ? (value) => percentageSignFilter()(value, 2)
                : (value) => minAbbrNumberFilter()(value),
        });
    }, [lineGraphData, selectedCurrency, isPercentage]);

    const GraphComponent = useMemo(() => {
        if (!isPercentage) {
            return LineGraph;
        }
        return AreaGraph;
    }, [graphType.value]);

    const handleLegendSelectionChange = (legendItems: GenericGraphProps) => {
        setLineGraphData((prevData) =>
            prevData.map((series) => {
                const updatedItem = legendItems.find((item) =>
                    series.id ? item.id === series.id : item.name === series.name,
                );
                return {
                    ...series,
                    visible: updatedItem?.visible ?? series.visible,
                };
            }),
        );
    };

    const trackLegendItemClicked = (legendItem: string, action: string) => {
        allTrackers.dispatchTrackEvent(
            "Click Filter",
            `${action === "add" ? "check" : "uncheck"}`,
            `Over Time Graph_PPC Spend/${legendItem}`,
        );
    };

    const onGraphTypeSwitcherClick = (index) => {
        const type = availableDataTypes[index].value;
        setGraphType(availableDataTypes[index]);

        allTrackers.dispatchTrackEvent("Measure Button", "click", `Over Time Graph_PPC Spend/${type}`);
    };

    if (isLoading) {
        return (
            <LoaderContainer>
                <LoadingSpinner />
            </LoaderContainer>
        );
    }

    if (isUnsupportedRange(error)) {
        return <UnsupportedRangeNotice />;
    }

    // @ts-expect-error FIXME: Type 'GenericGraphProps' is not assignable to parameter of type 'Pick<SeriesLineOptions, "data">[]
    if (isError || lineGraphData.length === 0 || isAllZeroSeries(lineGraphData)) {
        return (
            <ChartContainerNoData>
                <NoData />
            </ChartContainerNoData>
        );
    }

    const onCurrencyChange = (currency) => {
        setSelectedCurrency(currency);

        allTrackers.dispatchTrackEvent("Graph Filter", "click", `Over Time Graph_PPC Spend/Currency/${currency.name}`);

        const params = {
            performanceOverTimeChartCurrency: currency.name,
        };
        queueMicrotask(() => {
            proNavigator.applyUpdateParams(params, { reload: false });
        });
    };

    return (
        <PpcSpendChartWrapper data-automation="ppcSpendChart">
            <ButtonsContainer data-automation="ppc-spend-chart-buttons">
                <SwitchersContainer data-automation="ppc-spend-chart-switchers">
                    {isCompare ? (
                        <GraphTypeSwitcher
                            onItemClick={(index) => onGraphTypeSwitcherClick(index)}
                            selectedIndex={availableDataTypes.findIndex((item) => item.value === graphType.value)}
                            buttonsList={availableDataTypes}
                        />
                    ) : null}
                    {currenciesList ? (
                        <CurrencySelector
                            currencies={currenciesList}
                            onCurrencyChange={onCurrencyChange}
                            selectedCurrency={selectedCurrency}
                            withoutMargin={true}
                        />
                    ) : null}
                </SwitchersContainer>
            </ButtonsContainer>
            <GraphComponent
                // @ts-expect-error FIXME: GraphComponent should be rendered as AreaGraph or LineGraph separately with specific series data
                data={lineGraphData}
                shouldPresentLegend={isCompare}
                shouldPresentValueAtLegend
                withoutTooltipsPadding={false}
                legendPosition={LEGEND_POSITIONS.RIGHT}
                componentNameForTracking=""
                // @ts-expect-error FIXME: Type '(legendItems: GenericGraphProps) => void' is not assignable to type '(filterData: Data[]) => void'
                legendToggleHandlerCallback={handleLegendSelectionChange}
                legendValueFilter={
                    isPercentage ? (value) => percentageSignFilter()(value, 2) : (value) => abbrNumberFilter()(value)
                }
                graphConfig={chartConfig}
                legendsHeight={"100%"}
                legendsMaxWidth={250}
                legendGraphGap={24}
                customLegendTrackingHandler={trackLegendItemClicked}
            />
            <SpendRatesNote
                monthsWithoutRates={spend.monthsWithoutRates}
                currencyName={selectedCurrency.name}
            />
        </PpcSpendChartWrapper>
    );
};

export default PpcSpendChart;
